import React, { createContext, useContext, useState, ReactNode } from 'react';
import { Task } from '../../types';
import { toast } from 'sonner';

export interface QueuedDeleteTask extends Omit<Partial<Task>, 'status'> {
  id: string; // Task ID
  queued_at: string;
  status: 'queued' | 'deleting' | 'deleted' | 'failed';
  error?: string;
}

interface DeleteQueueContextType {
  queuedDeleteTasks: QueuedDeleteTask[];
  addTaskToDeleteQueue: (task: Task) => boolean;
  addTasksToDeleteQueue: (tasks: Task[]) => number;
  removeTaskFromDeleteQueue: (taskId: string) => void;
  clearDeleteQueue: () => void;
  clearCompletedDeletes: () => void;
  updateDeleteTaskStatus: (taskId: string, status: QueuedDeleteTask['status'], error?: string) => void;
  isDeleteQueueVisible: boolean;
  setDeleteQueueVisible: (visible: boolean) => void;
  isProcessingDelete: boolean;
  setIsProcessingDelete: (processing: boolean) => void;
}

const DeleteQueueContext = createContext<DeleteQueueContextType | undefined>(undefined);

export const useDeleteQueue = () => {
  const context = useContext(DeleteQueueContext);
  if (!context) {
    throw new Error('useDeleteQueue must be used within a DeleteQueueProvider');
  }
  return context;
};

interface DeleteQueueProviderProps {
  children: ReactNode;
}

export const DeleteQueueProvider: React.FC<DeleteQueueProviderProps> = ({ children }) => {
  const [queuedDeleteTasks, setQueuedDeleteTasks] = useState<QueuedDeleteTask[]>([]);
  const [isDeleteQueueVisible, setDeleteQueueVisible] = useState(false);
  const [isProcessingDelete, setIsProcessingDelete] = useState(false);

  const addTaskToDeleteQueue = (task: Task): boolean => {
    if (queuedDeleteTasks.some(t => t.id === task.id && t.status !== 'deleted' && t.status !== 'failed')) {
      toast.info(`Task "${task.title}" is already in delete queue`);
      return false;
    }
    const queuedTask: QueuedDeleteTask = {
      ...task,
      id: task.id,
      queued_at: new Date().toISOString(),
      status: 'queued',
    };
    setQueuedDeleteTasks(prev => [...prev, queuedTask]);
    setDeleteQueueVisible(true);
    return true;
  };

  // Bulk add function for adding multiple tasks (parent + subtasks)
  const addTasksToDeleteQueue = (tasks: Task[]): number => {
    const newTasks: QueuedDeleteTask[] = [];
    for (const task of tasks) {
      if (!queuedDeleteTasks.some(t => t.id === task.id && t.status !== 'deleted' && t.status !== 'failed')) {
        newTasks.push({
          ...task,
          id: task.id,
          queued_at: new Date().toISOString(),
          status: 'queued',
        });
      }
    }
    if (newTasks.length > 0) {
      setQueuedDeleteTasks(prev => [...prev, ...newTasks]);
      setDeleteQueueVisible(true);
    }
    return newTasks.length;
  };

  const removeTaskFromDeleteQueue = (taskId: string) => {
    setQueuedDeleteTasks(prev => prev.filter(task => task.id !== taskId));
  };
  const clearDeleteQueue = () => {
    setQueuedDeleteTasks([]);
    setDeleteQueueVisible(false);
  };
  const clearCompletedDeletes = () => {
    setQueuedDeleteTasks(prev => prev.filter(task => task.status !== 'deleted' && task.status !== 'failed'));
  };
  const updateDeleteTaskStatus = (taskId: string, status: QueuedDeleteTask['status'], error?: string) => {
    setQueuedDeleteTasks(prev => prev.map(task =>
      task.id === taskId ? { ...task, status, error } : task
    ));
  };

  return (
    <DeleteQueueContext.Provider value={{
      queuedDeleteTasks,
      addTaskToDeleteQueue,
      addTasksToDeleteQueue,
      removeTaskFromDeleteQueue,
      clearDeleteQueue,
      clearCompletedDeletes,
      updateDeleteTaskStatus,
      isDeleteQueueVisible,
      setDeleteQueueVisible,
      isProcessingDelete,
      setIsProcessingDelete
    }}>
      {children}
    </DeleteQueueContext.Provider>
  );
};
